
// frontend/src/slices/spsApiSlices.js
// ─────────────────────────────────────────────────────────────────────────────
// All SPS RTK Query endpoint slices injected into the shared apiSlice.
// Import the hooks you need in each screen component.
// ─────────────────────────────────────────────────────────────────────────────
 
import { apiSlice } from './apiSlice';
 
const CSP_URL   = '/api/csp';
const CPI_URL   = '/api/cpi';
const STAFF_URL = '/api/staff-directory';
const PAY_URL   = '/api/pay-records';
const CALC_URL  = '/api/calculator';
const EMAIL_URL = '/api/email';
 
// ═══════════════════════════ CSP SLICE ════════════════════════════════════════
export const cspApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCspYears: builder.query({
      query: () => ({ url: CSP_URL }),
      providesTags: ['Csp'],
      keepUnusedDataFor: 60,
    }),
    getCspByYear: builder.query({
      query: (year) => ({ url: `${CSP_URL}/${year}` }),
      providesTags: (r, e, year) => [{ type: 'Csp', id: year }],
    }),
    createCspYear: builder.mutation({
      query: (data) => ({ url: CSP_URL, method: 'POST', body: data }),
      invalidatesTags: ['Csp'],
    }),
    updateCspYear: builder.mutation({
      query: ({ year, ...data }) => ({ url: `${CSP_URL}/${year}`, method: 'PUT', body: data }),
      invalidatesTags: ['Csp'],
    }),
    deleteCspYear: builder.mutation({
      query: (year) => ({ url: `${CSP_URL}/${year}`, method: 'DELETE' }),
      invalidatesTags: ['Csp'],
    }),
    exportCspJson: builder.mutation({
      query: () => ({ url: `${CSP_URL}/export-json`, method: 'POST' }),
    }),
 
    // ── My Record ──────────────────────────────────────────────────────────
    getMyRecord: builder.query({
      query: () => '/api/my-record',
      providesTags: ['MyRecord'],
    }),
    updateMyRecord: builder.mutation({
      query: (data) => ({ url: '/api/my-record', method: 'PUT', body: data }),
      invalidatesTags: ['MyRecord'],
    }),
    sendNewEmailVerification: builder.mutation({
      query: (data) => ({ url: '/api/my-record/verify-new-email', method: 'POST', body: data }),
    }),
    confirmEmailChange: builder.mutation({
      query: (data) => ({ url: '/api/my-record/confirm-email-change', method: 'POST', body: data }),
      invalidatesTags: ['MyRecord'],
    }),

    // Inside the cspApiSlice endpoints block, alongside the other my-record mutations:
    raiseSchemeMembershipQuery: builder.mutation({
      query: (data) => ({ url: '/api/my-record/scheme-membership-query', method: 'POST', body: data }),
      // { raise: true | false }
      invalidatesTags: ['MyRecord'],
    }),

 
    // ── Password change (OTP-gated) ────────────────────────────────────────
    // Step 1: sends a 6-digit OTP to the logged-in user's email — no body needed
    sendPasswordResetOtp: builder.mutation({
      query: () => ({ url: '/api/my-record/send-password-otp', method: 'POST' }),
    }),
    // Step 2: verifies the OTP and saves the new hashed password to the users collection
    changePasswordWithOtp: builder.mutation({
      query: (data) => ({ url: '/api/my-record/change-password', method: 'POST', body: data }),
      // { otp: '123456', newPassword: '...' }
    }),
  }),
});
 
export const {
  useGetCspYearsQuery,
  useGetCspByYearQuery,
  useCreateCspYearMutation,
  useUpdateCspYearMutation,
  useDeleteCspYearMutation,
  useExportCspJsonMutation,
  useGetMyRecordQuery,
  useUpdateMyRecordMutation,
  useSendNewEmailVerificationMutation,
  useConfirmEmailChangeMutation,
  useSendPasswordResetOtpMutation,
  useChangePasswordWithOtpMutation,
  useRaiseSchemeMembershipQueryMutation,
} = cspApiSlice;
 
 
// ═══════════════════════════ CPI SLICE ════════════════════════════════════════
export const cpiApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCpiRates: builder.query({
      query: () => ({ url: CPI_URL }),
      providesTags: ['Cpi'],
      keepUnusedDataFor: 60,
    }),
    createCpiYear: builder.mutation({
      query: (data) => ({ url: CPI_URL, method: 'POST', body: data }),
      invalidatesTags: ['Cpi'],
    }),
    updateCpiYear: builder.mutation({
      query: ({ year, ...data }) => ({ url: `${CPI_URL}/${year}`, method: 'PUT', body: data }),
      invalidatesTags: ['Cpi'],
    }),
    exportCpiJson: builder.mutation({
      query: () => ({ url: `${CPI_URL}/export-json`, method: 'POST' }),
    }),
  }),
});
 
export const {
  useGetCpiRatesQuery,
  useCreateCpiYearMutation,
  useUpdateCpiYearMutation,
  useExportCpiJsonMutation,
} = cpiApiSlice;
 
 
// ═══════════════════════ STAFF DIRECTORY SLICE ════════════════════════════════
export const staffDirectoryApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // params: { page, limit, search, isActive, hasEmail }
    getStaffDirectory: builder.query({
      query: (params = {}) => ({ url: STAFF_URL, params }),
      providesTags: ['StaffDir'],
    }),
    getStaffByPps: builder.query({
      query: (pps) => ({ url: `${STAFF_URL}/${pps}` }),
      providesTags: (r, e, pps) => [{ type: 'StaffDir', id: pps }],
    }),
    updateStaffMember: builder.mutation({
      query: ({ pps, ...data }) => ({ url: `${STAFF_URL}/${pps}`, method: 'PUT', body: data }),
      invalidatesTags: (r, e, { pps }) => [{ type: 'StaffDir', id: pps }, 'StaffDir'],
    }),
    syncFromPayData: builder.mutation({
      query: () => ({ url: `${STAFF_URL}/sync-from-pay`, method: 'POST' }),
      invalidatesTags: ['StaffDir'],
    }),
    getSyncSummary: builder.query({
      query: () => ({ url: `${STAFF_URL}/sync-summary` }),
    }),
    syncBatch: builder.mutation({
      query: (data) => ({ url: `${STAFF_URL}/sync-batch`, method: 'POST', body: data }),
    }),
    getEmailHistory: builder.query({
      query: (pps) => ({ url: `${STAFF_URL}/${pps}/email-history` }),
      providesTags: (r, e, pps) => [{ type: 'EmailLog', id: pps }],
    }),
    importDemographics: builder.mutation({
      query: (formData) => ({
        url:      `${STAFF_URL}/import-demographics`,
        method:   'POST',
        body:     formData,
        formData: true,
      }),
      invalidatesTags: ['StaffDir'],
    }),
  }),
});
 
export const {
  useGetStaffDirectoryQuery,
  useGetStaffByPpsQuery,
  useUpdateStaffMemberMutation,
  useSyncFromPayDataMutation,
  useGetSyncSummaryQuery,
  useSyncBatchMutation,
  useGetEmailHistoryQuery,
  useImportDemographicsMutation,
} = staffDirectoryApiSlice;
 
 
// ═══════════════════════ PAY RECORDS SLICE ════════════════════════════════════
export const payRecordsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPayRecords: builder.query({
      query: (params = {}) => ({ url: PAY_URL, params }),
      providesTags: ['PayRecord'],
    }),
    getPayRecordsByPps: builder.query({
      query: (pps) => ({ url: `${PAY_URL}/${pps}` }),
      providesTags: (r, e, pps) => [{ type: 'PayRecord', id: pps }],
    }),
    getPayDataSummary: builder.query({
      query: () => ({ url: `${PAY_URL}/summary` }),
      providesTags: ['PayRecord'],
      keepUnusedDataFor: 120,
    }),
    importPayData: builder.mutation({
      query: (formData) => ({
        url:    `${PAY_URL}/import`,
        method: 'POST',
        body:   formData,
        formData: true,
      }),
      invalidatesTags: ['PayRecord', 'StaffDir'],
    }),
    updatePayRecord: builder.mutation({
      query: ({ id, ...data }) => ({ url: `${PAY_URL}/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['PayRecord'],
    }),
  }),
});
 
export const {
  useGetPayRecordsQuery,
  useGetPayRecordsByPpsQuery,
  useGetPayDataSummaryQuery,
  useImportPayDataMutation,
  useUpdatePayRecordMutation,
} = payRecordsApiSlice;
 
 
// ═══════════════════════ CALCULATOR SLICE ═════════════════════════════════════
export const calculatorApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    runIndividual: builder.mutation({
      query: (pps) => ({ url: `${CALC_URL}/run-one/${pps}`, method: 'POST' }),
      invalidatesTags: ['RunHistory', 'StaffDir'],
    }),
    // runBatch is handled via EventSource (SSE), not RTK Query
    // See RunCalculatorScreen for the SSE implementation
    getRunHistory: builder.query({
      query: (params = {}) => ({ url: `${CALC_URL}/runs`, params }),
      providesTags: ['RunHistory'],
    }),
    getRunDetail: builder.query({
      query: (id) => ({ url: `${CALC_URL}/runs/${id}` }),
      providesTags: (r, e, id) => [{ type: 'RunHistory', id }],
    }),
  }),
});
 
export const {
  useRunIndividualMutation,
  useGetRunHistoryQuery,
  useGetRunDetailQuery,
} = calculatorApiSlice;
 
 
// ═══════════════════════ EMAIL SLICE ══════════════════════════════════════════
export const emailApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    sendToOne: builder.mutation({
      query: (pps) => ({ url: `${EMAIL_URL}/send-one/${pps}`, method: 'POST' }),
      invalidatesTags: ['EmailLog', 'StaffDir'],
    }),
    sendBatch: builder.mutation({
      query: (data) => ({ url: `${EMAIL_URL}/send-batch`, method: 'POST', body: data }),
      invalidatesTags: ['EmailLog', 'StaffDir'],
    }),
    getEmailLog: builder.query({
      query: (params = {}) => ({ url: `${EMAIL_URL}/log`, params }),
      providesTags: ['EmailLog'],
    }),
    getEmailLogByPps: builder.query({
      query: (pps) => ({ url: `${EMAIL_URL}/log/${pps}` }),
      providesTags: (r, e, pps) => [{ type: 'EmailLog', id: pps }],
    }),
  }),
});
 
export const {
  useSendToOneMutation,
  useSendBatchMutation,
  useGetEmailLogQuery,
  useGetEmailLogByPpsQuery,
} = emailApiSlice;